import { useState, useEffect, useMemo, type CSSProperties } from "react";
import { useWorkspaceMembers } from "../../hooks/use-workspace-members";
import { useSetNodeAccess, useSetTableAccess } from "../../hooks/use-node-access";
import { Avatar, showToast } from "../primitives";
import { ScopeChangeWarningModal } from "./ScopeChangeWarningModal";

/**
 * Sprint 14c — Manage access (S14C.32).
 *
 * Restrict a node (agent / folder / page / memory) or a whole table to specific
 * workspace members, or clear the restriction. Owners are always shown but their
 * checkbox is disabled — they retain access no matter what. Saving a WIDENING
 * change routes through {@link ScopeChangeWarningModal} for confirmation first.
 */
export interface ManageAccessModalProps {
  open: boolean;
  onClose: () => void;
  /** Display name of the target. */
  name: string;
  /** Target a library node (set_node_access). */
  nodeId?: string;
  /** Or target a table by entity-type slug (set_table_access). */
  entityTypeSlug?: string;
  /** Current restriction (null/[] = workspace-visible). */
  currentAllowedUsers?: string[] | null;
}

export function ManageAccessModal({
  open,
  onClose,
  name,
  nodeId,
  entityTypeSlug,
  currentAllowedUsers,
}: ManageAccessModalProps) {
  const { data } = useWorkspaceMembers();
  const members = useMemo(() => data?.members ?? [], [data]);
  const setNodeAccess = useSetNodeAccess();
  const setTableAccess = useSetTableAccess();

  const initialRestricted = Array.isArray(currentAllowedUsers) && currentAllowedUsers.length > 0;
  const [restricted, setRestricted] = useState(initialRestricted);
  const [selected, setSelected] = useState<Set<string>>(new Set(currentAllowedUsers ?? []));
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState<{ message: string } | null>(null);

  useEffect(() => {
    if (open) {
      const r = Array.isArray(currentAllowedUsers) && currentAllowedUsers.length > 0;
      setRestricted(r);
      setSelected(new Set(currentAllowedUsers ?? []));
      setBusy(false);
      setConfirm(null);
    }
  }, [open, currentAllowedUsers]);

  if (!open) return null;

  const owners = members.filter((m) => m.role === "owner");
  const nonOwners = members.filter((m) => m.role !== "owner");

  function toggle(userId: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  }

  // The audience we'll persist: non-owner selected members (owners are implicit).
  const audience = restricted
    ? nonOwners.filter((m) => selected.has(m.id)).map((m) => m.id)
    : null;

  /** Does this change make content visible to MORE people than before? */
  function isWidening(): boolean {
    const before = currentAllowedUsers;
    const beforeRestricted = Array.isArray(before) && before.length > 0;
    if (!beforeRestricted) return false; // already workspace-visible — can't widen
    if (!restricted) return true; // restricted → everyone
    const beforeSet = new Set(before);
    return (audience ?? []).some((u) => !beforeSet.has(u));
  }

  async function apply() {
    setBusy(true);
    try {
      const allowed = audience && audience.length > 0 ? audience : null;
      const res = entityTypeSlug
        ? await setTableAccess.mutateAsync({ entity_type_slug: entityTypeSlug, allowed_users: allowed })
        : await setNodeAccess.mutateAsync({ node_id: nodeId!, allowed_users: allowed });
      const warning = res._warnings?.find((w) => w.kind === "scope_change");
      showToast(
        allowed ? `Restricted '${name}' to ${allowed.length} member(s)` : `'${name}' is workspace-visible again`,
      );
      if (warning) showToast(warning.message, "success", true);
      onClose();
    } catch (err) {
      showToast(err instanceof Error ? err.message : "Couldn't update access", "error", true);
    } finally {
      setBusy(false);
      setConfirm(null);
    }
  }

  function handleSave() {
    if (restricted && (audience?.length ?? 0) === 0) {
      // Restricted but nobody picked → that clears the restriction; confirm intent.
      setConfirm({
        message:
          "No members selected — this will make the content visible to everyone in the workspace. Continue?",
      });
      return;
    }
    if (isWidening()) {
      const count = restricted ? (audience?.length ?? 0) : members.length;
      setConfirm({
        message: !restricted
          ? `This removes the restriction on '${name}'. Everyone in the workspace will be able to see it and everything inside.`
          : `This widens access to '${name}' to ${count} member(s). More people will be able to see it and everything inside.`,
      });
      return;
    }
    void apply();
  }

  const overlay: CSSProperties = {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.3)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 100,
    fontFamily: "var(--font-sans)",
  };
  const modal: CSSProperties = {
    background: "var(--color-background-primary)",
    border: "var(--kuvio-border-default)",
    borderRadius: "var(--border-radius-lg)",
    padding: 24,
    width: 460,
    maxWidth: "92vw",
    maxHeight: "82vh",
    display: "flex",
    flexDirection: "column",
  };

  return (
    <>
      <div style={overlay} onClick={(e) => e.target === e.currentTarget && !busy && onClose()}>
        <div style={modal} role="dialog" aria-label="Manage access">
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              fontSize: 15,
              fontWeight: 500,
              marginBottom: 6,
              color: "var(--color-text-primary)",
            }}
          >
            <i className="ti ti-lock" aria-hidden="true" />
            Manage access — {name}
          </div>
          <div style={{ fontSize: 13, color: "var(--color-text-secondary)", lineHeight: 1.5, marginBottom: 14 }}>
            Restrict who can see this{entityTypeSlug ? " table and all its rows" : " and everything inside it"}.
            Owners always have access.
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 12 }}>
            <label style={radioRow}>
              <input
                type="radio"
                checked={!restricted}
                onChange={() => setRestricted(false)}
              />
              <span>
                <strong style={{ fontWeight: 500 }}>Workspace-visible</strong>
                <span style={hint}>Everyone in the workspace can see it.</span>
              </span>
            </label>
            <label style={radioRow}>
              <input type="radio" checked={restricted} onChange={() => setRestricted(true)} />
              <span>
                <strong style={{ fontWeight: 500 }}>Restricted</strong>
                <span style={hint}>Only the people you pick below (plus owners).</span>
              </span>
            </label>
          </div>

          {restricted && (
            <div style={{ overflowY: "auto", flex: 1, border: "var(--kuvio-border-default)", borderRadius: "var(--border-radius-md)" }}>
              {owners.map((m) => (
                <MemberRow key={m.id} m={m} checked disabled tooltip="Owners always have access" />
              ))}
              {nonOwners.map((m) => (
                <MemberRow
                  key={m.id}
                  m={m}
                  checked={selected.has(m.id)}
                  disabled={false}
                  onToggle={() => toggle(m.id)}
                />
              ))}
              {members.length === 0 && (
                <div style={{ padding: 12, fontSize: 12, color: "var(--color-text-tertiary)" }}>
                  No members yet.
                </div>
              )}
            </div>
          )}

          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 16 }}>
            <button type="button" onClick={onClose} disabled={busy} style={{ fontSize: 13, padding: "8px 14px" }}>
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={busy}
              style={{
                fontSize: 13,
                padding: "8px 14px",
                borderRadius: "var(--border-radius-md)",
                border: "none",
                fontWeight: 500,
                fontFamily: "var(--font-sans)",
                cursor: busy ? "default" : "pointer",
                background: "var(--c-blue-600, var(--color-accent, #2563eb))",
                color: "#fff",
                opacity: busy ? 0.7 : 1,
              }}
            >
              {busy ? "Saving…" : "Save access"}
            </button>
          </div>
        </div>
      </div>

      <ScopeChangeWarningModal
        open={!!confirm}
        message={confirm?.message ?? ""}
        confirmLabel="Apply"
        busy={busy}
        onConfirm={() => void apply()}
        onCancel={() => setConfirm(null)}
      />
    </>
  );
}

const radioRow: CSSProperties = {
  display: "flex",
  alignItems: "flex-start",
  gap: 8,
  fontSize: 13,
  color: "var(--color-text-primary)",
  cursor: "pointer",
};
const hint: CSSProperties = {
  display: "block",
  fontSize: 12,
  color: "var(--color-text-tertiary)",
};

function MemberRow({
  m,
  checked,
  disabled,
  tooltip,
  onToggle,
}: {
  m: { id: string; name: string; email: string };
  checked: boolean;
  disabled: boolean;
  tooltip?: string;
  onToggle?: () => void;
}) {
  return (
    <label
      title={tooltip}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "8px 12px",
        fontSize: 13,
        cursor: disabled ? "default" : "pointer",
        opacity: disabled ? 0.7 : 1,
        borderBottom: "0.5px solid var(--color-border-secondary, transparent)",
      }}
    >
      <input type="checkbox" checked={checked} disabled={disabled} onChange={onToggle} />
      <Avatar persona="gray" size="xs" name={m.name || m.email} />
      <span style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
        <span style={{ color: "var(--color-text-primary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {m.name || m.email}
        </span>
        {m.name && (
          <span style={{ fontSize: 11, color: "var(--color-text-tertiary)" }}>{m.email}</span>
        )}
      </span>
    </label>
  );
}
